<template>
  <button
    class="watch-btn"
    :aria-label="(watchStatus === true ? $t('common:share.removeFromWatchlist') :$t('common:share.addToWatchlist' ))"
    :class="{
      [extraClass]: extraClass,
      'active': watchStatus,
      'inactive': !watchStatus,
    }"
    type="button"
    @click.stop="watchObjectAction"
  >
    <i class="icon">
      <transition-group
        name="watch"
        class="watch-icon-transgrp"
      >
        <icon-heart-alternative
          v-show="!watchStatus"
          :key="'watch-add-light'"
          class="svg-icon svg-fill watch-icon-light"
        />
        <icon-heart-alternative-two
          v-show="watchStatus"
          :key="'watch-add-solid'"
          class="svg-icon svg-fill watch-icon-solid"
        />
        <icon-heart-broken
          v-show="!watchStatus"
          :key="'watch-add-broken'"
          class="svg-icon svg-fill watch-icon-broken"
        />
      </transition-group>
    </i>
    <b-toast
      v-if="renderToast"
      id="watch-failed"
      ref="watchFailed"
      title="Merken fehlgeschlagen"
      :variant="'warning'"
      :toaster="'b-toaster-top-right'"
    >
      <div>{{ $t('user:watchList.error.watchFailed') }}</div>
    </b-toast>
  </button>
</template>

<script>
import { BToast } from 'bootstrap-vue';

import IconHeartAlternative from 'ICON:IconHeartAlternative';
import IconHeartAlternativeTwo from 'ICON:IconHeartAlternativeTwo';
import IconHeartBroken from 'ICON:IconHeartBroken';

export default {
  name: 'WatchBtn',
  i18nOptions: { namespaces: ['user'] },
  components: {
    BToast,
    IconHeartAlternative,
    IconHeartAlternativeTwo,
    IconHeartBroken,
  },
  props: {
    objectNumber: {
      required: true,
      type: [Number, String],
      validator(value) {
        return value === 0 || parseInt(value, 10);
      },
    },
    extraClass: {
      type: String,
      required: false,
      default: '',
    },
    minPrice: {
      type: Number,
      required: false,
      default: 0,
    },
    priceObject: {
      type: Object,
      required: false,
      default() {
        return {
          price: this.minPrice > 0 ? this.minPrice : null,
          original: null,
          pricePercentReduction: null,
          currency: 'EUR',
          startDate: null,
          endDate: null,
          persons: null,
        };
      },
    },
  },
  data() {
    return {
      renderToast: false,
    };
  },
  computed: {
    watchedObjects() {
      return this.$store.getters.getWatchedObjects;
    },
    watchStatus() {
      let status = false;
      this.watchedObjects.forEach((watchedObject) => {
        if (watchedObject.objectNumber === this.objectNumber && !watchedObject.deleteDate) {
          status = true;
        }
      });
      return status;
    },
    watchListUID() {
      return this.$store.getters.getWatchListUID;
    },
  },
  mounted() {
    this.renderToast = true;
  },
  methods: {
    watchObjectAction() {
      this.$store.dispatch('UPDATE_WATCHED_OBJECTS', {
        objectData: {
          objectNumber: this.objectNumber,
          deleteDate: null,
          ...this.priceObject,
        },
        action: this.watchStatus ? 'delete' : 'add',
      }).catch(() => {
        this.$refs.watchFailed.show();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.watch-btn {
  width: 2em;
  height: 2em;
  line-height: 1em;
  position: absolute;
  top: 15px;
  right: 0;
  background: transparent;
  border: none;
  z-index: 30;
  outline: none;

  &.active i {
    color: $color_watch_active;
  }

  i {
    height: inherit;
    width: inherit;
    color: $color_watch;
    position: absolute;
    top: 0;
    right: 0;

    &.icon {
      @include drop_shadow_strong;

      .watch-icon-transgrp {
        @include drop_shadow_none;

        position: relative;
        width: inherit;
        height: inherit;

        .watch-icon-light,
        .watch-icon-solid,
        .watch-icon-broken {
          position: absolute;
          top: 0.5em;
          left: 50%;
          margin: 0 0 0 -0.5em;
        }
      }
    }
  }

  &.watch-icon-light,
  #app:not(.isTouchDevice) &:hover {
    .watch-icon-solid {
      &:not(.watch-leave-active) {
        display: inline-block !important;
      }
    }

    .watch-icon-light {
      display: none;
    }
  }

  /* extra styles for different watch buttons */
  &.watch-btn-search {
    @include font_size_xl;

    right: 0;

    @media #{$media_xs} {
      top: 0;

      @include font_size_xxl;
    }

    @media #{$media_sm} {
      @include font_size_xxl;

      right: 20px;
    }

    @media #{$media_xxl} {
      @include font_size_xxl;
    }
  }

  &.watch-btn-last-seen-object {
    right: 0;
    top: 0;

    @include font_size_xl;
  }

  &.watch-btn-object {
    z-index: 1101;
    display: inline-block;
    border: none;
    top: 10px;

    @include transition_bg_fast;
    @include radius(100%);
    @include font_size_xxl;

    .headerMain_invisible & {
      @include drop_shadow_none;

      position: fixed;
      background: var(--color_ci);
      top: 73px;
      left: 50%;

      @media #{$media_xs} {
        left: auto;
        right: 15px;
        top: 55px;
      }

      @media #{$media_sm} {
        margin-left: 204px;
      }

      @media #{$media_md} {
        margin-left: 291px;
      }

      @media #{$media_lg} {
        margin-left: 84px;
      }

      @media #{$media_xl} {
        margin-left: 158px;
      }

      @media #{$media_xxl} {
        margin-left: 255px;
      }

      i.icon {
        top: 0;
        margin: 0;

        @include drop_shadow_none;
      }
    }
  }
}

/* transition on watch add/delete */
.watch-icon-transgrp {
  display: block;
  line-height: 2em;
  text-align: center;

  .watch-enter-active,
  .watch-leave-active {
    transition: all 0s, opacity 0.8s;
  }

  .watch-icon-light {
    &.watch-enter {
      opacity: 0;
    }

    &.watch-enter-to {
      opacity: 1;
    }

    &.watch-leave {
      opacity: 1;
    }

    &.watch-leave-to {
      opacity: 0;
    }
  }

  .watch-icon-solid {
    &.watch-leave-active {
      display: none;
    }

    &.watch-enter {
      opacity: 0;
    }

    &.watch-enter-to {
      opacity: 1;
    }
  }

  .watch-icon-broken {
    display: none;

    &.watch-enter-active {
      display: inline-block;
    }

    &.watch-enter {
      opacity: 1;
    }

    &.watch-enter-to {
      opacity: 0;
    }
  }
}
</style>
